import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useParams, useLocation, useHistory } from 'react-router-dom';

import { Container } from '@mui/material';
import { DashboardState } from '../../../interfaces/idashboard';
import { DashboardPropTypes } from '../../../interfaces/iproptypes';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TutorialModal from './Modals/TutorialModal';
import {
  TutorialType,
  isValidTutorialQueryValue,
} from './Modals/tutorialConfig';
import {
  getMarketingTrackers,
  getUniqueUserId,
  clearApiCache,
  getUserPreferredCurrency,
} from 'src/utils/functions';
import { useOpenOverviewOnboardingPage } from 'src/utils/hooks';
import {
  OnboardingModalMobile,
  SubscriptionModal,
  AuthModal,
  UpgradeSubscriptionModal,
  UpgradeYearlyPlanModal,
} from './Modals';
import { Header, Sidebar } from './Components';
import '../Dashboard.scss';
import {
  DARK_GREY,
  BACKGROUND_COLOR,
  DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE,
  special_country_code_currency_map,
  sidebarClosedWidth,
  HEADER_HEIGHT,
  MARKET_SEARCH_HISTORY,
  FREE_MARKET_SEARCH_LIMIT,
  MARKET_HOME_CHAT_PATH,
  HOME_CHAT_SIDEBAR_LIST_INDEX,
} from 'src/utils/const';
import { callApi } from 'src/core/callApi';
import { setUserCountryCode, getLastPerformanceProcessedMonth } from 'src/store/Dashboard/actions';
import EmailVerifyModal from 'src/App/Dashboards/Market/Modals/EmailVerifyModal';
import postOnSlack from 'src/core/postOnSlack';
import MarketSearchBar from 'src/App/Dashboards/Market/Components/Header/MarketSearchBar';
import { timeZoneCCMap } from '../../assets/timeZoneCCMap';

import OverviewLandingPage from 'src/App/Dashboards/Market/Pages/OverviewPage/OverviewLandingPage';
import { OverviewPage } from 'src/App/Dashboards/Market/Pages/OverviewPage';
import SearchListings from 'src/App/Dashboards/Market/Pages/Features/SearchListings/SearchListings';
import DemoBookingForm from 'src/App/Dashboards/Market/Components/Forms/DemoBookingForm';
import { EstimatorPage } from 'src/App/Dashboards/Market/Pages/EstimatorPage';
import { useSubscription } from 'src/hooks/useCustomHooks';
import { useIdentifyAmplitudeUser } from 'src/hooks/useProductAnalytics';
import IndividualListingPage from 'src/App/Dashboards/Market/Pages/Features/IndividualListingPage';
import IndividualHostPage from 'src/App/Dashboards/Market/Pages/Features/IndividualHostPage';
import SettingsDashboardV2 from 'src/App/Dashboards/Market/Pages/Features/SettingsV2/SettingsDashboardV2';
import HomeChatPage from 'src/App/Dashboards/Market/Pages/HomeChatPage/HomeChatPage';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: BACKGROUND_COLOR,
      boxShadow: `inset 0px 5px 10px 0px ${DARK_GREY}`,
      padding: 0,
      overflow: 'hidden',
      maxWidth: 4600,
      [theme.breakpoints.up(900)]: {
        marginLeft: sidebarClosedWidth,
        transition: theme.transitions.create('padding', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      },
    },
    SEOLinkStyle: {
      position: 'absolute',
      width: 1,
      height: 1,
      overflow: 'hidden',
      clip: 'rect(1px, 1px, 1px, 1px)',
      whiteSpace: 'nowrap',
    },
  })
);

// consts
const setMarketingTrackingURL =
  'https://4b5jgzfm05.execute-api.us-east-2.amazonaws.com/default/setMarketingTracking';

const MarketDashboard = ({
  tailoredRegionMarkers,
  search,
  user,
  currency,
  getTokenThunk,
  getUserInfo,
  getTailoredRegionMarkers,
  setCurrency,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  getCachedSearchMarkets,
  cachedSearchMarkets,
  cachedTop10MarketCountryWise,
  getTop10MarketCountryWise,
}: DashboardPropTypes) => {
  const classes = useStyles();
  const isLoggedIn = Boolean(getTokenThunk());
  const location = useLocation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const dispatch = useDispatch();
  const history = useHistory();
  useIdentifyAmplitudeUser();

  const openSubmarketPageFromURL = new URLSearchParams(window.location.search).get(
    'open_submarket_page'
  );

  const [overviewDashboardLayoutView, setOverviewDashboardLayoutView] = useState<
    'dashboard' | 'map' | 'compare' | 'searchListing' | 'submarkets' | 'marketIntelligence'
  >(openSubmarketPageFromURL ? 'submarkets' : 'dashboard');

  const appVisitCount = window.localStorage.getItem('appVisitCount');
  const pathname = window.location.pathname;
  const isHomeChatRoute =
    pathname === MARKET_HOME_CHAT_PATH || pathname === `${MARKET_HOME_CHAT_PATH}/`;

  // url params
  let { country, city, district, isTailored, settings } = useParams<{
    country: string;
    city: string;
    district: string;
    isTailored: string;
    settings: string;
  }>();

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let loginCountryCode = timeZoneCCMap[userTimeZone] || 'US';

  useEffect(() => {
    if (settings && settings === 'settings') {
      handleMarketSidebarClick('settings');
    }
  }, []);

  useEffect(() => {
    if (!appVisitCount) {
      window.localStorage.setItem('appVisitCount', String(1));
    } else {
      window.localStorage.setItem('appVisitCount', String(Number(appVisitCount) + 1));
    }
  }, []);

  useEffect(() => {
    let tailorRegionName = window.localStorage.getItem('tailoredRegion');

    if (tailorRegionName && isLoggedIn) {
      history.push(`/airbnb-data/0/0/${tailorRegionName}`);
      window.location.reload();
      localStorage.removeItem('tailoredRegion');
    }
  }, []);

  useEffect(() => {
    if (!Intl || !Intl.DateTimeFormat) {
      console.log('Intl API is not available in this environment.');
      dispatch(setUserCountryCode('US'));
    }
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const countryCode = timeZoneCCMap[userTimeZone] || 'US';

    if (special_country_code_currency_map[countryCode]) {
      setCurrency(getUserPreferredCurrency(special_country_code_currency_map[countryCode]));
    }

    dispatch(setUserCountryCode(countryCode));
  }, [Intl]);

  useEffect(() => {
    let userNameFromLocalstorage = window.localStorage.getItem('user-name');
    let userEmailFromLocalstorage = window.localStorage.getItem('user-email');

    if (user.email !== 'Trial' && user.email !== userEmailFromLocalstorage) {
      // Checking if the email from the localstorage and current emails are same or not
      //if not, updating the localstorage
      window.localStorage.setItem('user-email', user.email);
      userEmailFromLocalstorage = user.email;
    }

    if (user.userName !== 'Trial' && user.userName !== userNameFromLocalstorage) {
      // Checking if the name from the localstorage and current name are same or not
      //if not, updating the localstorage
      window.localStorage.setItem('user-name', user.userName);
      userNameFromLocalstorage = user.userName;
    }

    if ((window as any).Tawk_API) {
      (window as any).Tawk_API.onLoad = () => {
        (window as any).Tawk_API.setAttributes(
          {
            name: userNameFromLocalstorage,
            email: userEmailFromLocalstorage,
          },
          error => {
            console.error('Tawk API Error:', error);
          }
        );
        (window as any).Tawk_API.hideWidget();
      };
    }
  }, [user.email]);

  // query params
  const searchParams = location.search;
  const langQuery = new URLSearchParams(searchParams).get('lang') || '';
  const pageQuery = new URLSearchParams(searchParams).get('page') || '';
  const typeQuery = new URLSearchParams(searchParams).get('type') || '';
  const regionQuery = new URLSearchParams(searchParams).get('region') || '';
  const affiliatedByQuery = new URLSearchParams(searchParams).get('affiliatedBy') || '-1';

  const isOverviewLanding = pathname === '/airbnb-data/landing';
  const isIndividualLandingPage = pathname.includes('/listing/');
  const isIndividualHostPage = pathname.includes('/host/');
  const isTailoredRegionPage = pathname.includes('/tailored/t');

  const referrer = document.referrer;
  const [langTracker, pageTracker, typeTracker, regionTracker] = getMarketingTrackers();
  if (!langTracker && !pageTracker && !typeTracker && !regionTracker) {
    localStorage.setItem(
      'marketing-trackers',
      JSON.stringify({
        lang: langQuery,
        page: pageQuery ? pageQuery : referrer,
        type: typeQuery,
        region: regionQuery,
      })
    );
  }

  // legacy estimator query
  const estimatorAddressQuery = new URLSearchParams(searchParams).get('estimatorAddress');

  // new estimator queries
  const estimatorAddressFullQuery = new URLSearchParams(searchParams).get('address-full');
  const estimatorAddressFshortQuery = new URLSearchParams(searchParams).get('address-short');
  const estimatorBedroomsQuery = new URLSearchParams(searchParams).get('bedrooms');
  const estimatorReportTypeQuery = new URLSearchParams(searchParams).get('report-type');
  const estimatorPropertyPriceQuery = new URLSearchParams(searchParams).get('property-price');
  const estimatorMontlyRentQuery = new URLSearchParams(searchParams).get('monthly-rent');
  const estimatorLatQuery = new URLSearchParams(searchParams).get('lat');
  const estimatorLngQuery = new URLSearchParams(searchParams).get('lng');
  const dataStoreTimePeriodQuery = new URLSearchParams(searchParams).get(
    'listing-export-time-period'
  );
  const dataStoreTimeGranularityQuery = new URLSearchParams(searchParams).get(
    'listing-export-granularity'
  );

  useEffect(() => {
    if (search.data && search.data.currency) {
      setCurrency(getUserPreferredCurrency(search.data.currency));
    }
  }, [search.data]);

  // declarations
  const [landing, setLanding] = useState('investing');
  const states = require('us-state-codes');
  const cityName =
    search.data.countryCode == 'US' || country == 'united-states'
      ? states.getStateNameByStateCode(city)
      : search.data.city;

  const [dashboardState, setdashboardState] = React.useState({
    showInsightsDashboard: !isHomeChatRoute,
    showEstimatorDashboard: false,
    showSettingsDashboard: false,
    showTailoredRegion: false,
    showSearchListingDashboard: false,
    showHomeDashboard: isHomeChatRoute,
  } as DashboardState);
  const isHomePage = window.location.pathname === '/';
  const [headerExtraContent, setHeaderExtraContent] = useState<React.ReactNode>(null);

  // Redirect user to dummy market dashboard if hitting '/
  if (isHomePage) {
    // this below if condition is to keep track that user came from which affiliated partner
    if (affiliatedByQuery !== '-1') {
      localStorage.setItem('affiliatedKey', affiliatedByQuery);
    }
    window.location.href =
      window.location.origin + '/airbnb-data' + window.location.search + window.location.hash;
  }

  const [selectedSidebarIndex, setSelectedSidebarIndex] = useState(
    window.location.pathname === '/'
      ? -1
      : isHomeChatRoute
      ? HOME_CHAT_SIDEBAR_LIST_INDEX
      : 0
  );
  const [isTailoredRegionOpen, setIsTailoredRegionOpen] = React.useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [isEmailVerifyModalOpen, setIsEmailVerifyModalOpen] = useState<boolean>(false);
  const [isUserVerified, setIsUserVerified] = useState<boolean>(true);

  const {
    subscriptionModalOpen,
    setSubscriptionModalOpen,
    requiredSubscriptionPlan,
    setRequiredSubscriptionPlan,
    subscriptionModalView,
    setSubscriptionModalView,
    subscriptionModalOrigin,
    setSubscriptionModalOrigin,
    showUpgradeSubscriptionModal,
    setShowUpgradeSubscriptionModal,
    showSearchCreditDeductWarningModal,
    setShowSearchCreditDeductWarningModal,
    handleOnSampleClick,
    showTrial,
    setShowTrial,
    resetSubscriptionModal,
  } = useSubscription(isLoggedIn, setLoginModalOpen);

  const [showDemoBookingForm, setShowDemoBookingForm] = useState<boolean>(false);
  const [demoFormHeadingFor, setDemoFormHeadingFor] = useState<string>('demo');
  const [searchBaseCC, setSearchBaseCC] = useState(loginCountryCode);

  const [showUpgradeYearlySubscriptionModal, setShowUpgradeYearlySubscriptionModal] = useState<
    boolean
  >(false);

  const dummyMarket =
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE[loginCountryCode]?.overview ||
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE['US']?.overview;

  // Edge case handled where Dummay data is not present for user signup country So we made US as fallback country
  if (dummyMarket.district == 'gatlinburg') {
    loginCountryCode = 'US';
  }
  const dummyMarketEstimator =
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE[loginCountryCode]?.estimator ||
    DUMMY_MARKET_AS_PER_SIGNUP_COUNTRY_CODE['US']?.estimator;

  // State Variable for Estimator
  const [centerCoord, setCenterCoord] = useState<any>(dummyMarketEstimator?.coordinates);
  const [address, setAddress] = useState<string>('');

  const [openReportComparePage, setOpenReportComparePage] = useState<boolean>(false);

  const cachedMarketData = useSelector(state => (state as any).dashboard.cachedMarketData);

  const openOverviewOnboardingPage = useOpenOverviewOnboardingPage();

  useEffect(() => {
    const savedUserPrefCountryCode = localStorage.getItem('user-pref-country-code') || '';
    setSearchBaseCC(savedUserPrefCountryCode ? savedUserPrefCountryCode : loginCountryCode);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let appVisitCount = localStorage.getItem('appVisitCount');
      let affiliatedKey = localStorage.getItem('affiliatedKey');

      if (appVisitCount === '2') {
        const url =
          'https://ak27jez2w0.execute-api.us-east-2.amazonaws.com/default/affiliatePartnerStats';
        const config = {
          queryParams: {
            operationType: 'update',
            updatedKey: 'visitors_count',
            affiliateKey: affiliatedKey,
          },
        };

        if (affiliatedKey) {
          const res = await callApi(url, config, false);
        }
      }

      clearApiCache();
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (user.isVerified != isUserVerified) {
      setIsUserVerified(user.isVerified);
    }
  }, [user, user.isVerified]);

  // This UseEffect is responsible to show Email verification modal when after user search for 3 markets or after 1hr of signup
  useEffect(() => {
    let totalSearchDoneByUser =
      Object.keys(JSON.parse(window.localStorage.getItem(MARKET_SEARCH_HISTORY) || '{}')).length ||
      0;
    if (
      isUserVerified === false &&
      totalSearchDoneByUser >= FREE_MARKET_SEARCH_LIMIT &&
      user.isSubscribed == false &&
      !user.loading
    ) {
      setIsEmailVerifyModalOpen(true);
      handleOnSampleClick('expert');
    }
    if (isUserVerified === false && user.isSubscribed == false) {
      setTimeout(() => {
        setIsEmailVerifyModalOpen(true);
      }, 3600000); //1 hr
    }
    if (isUserVerified) {
      setIsEmailVerifyModalOpen(false);
    }
  }, [isUserVerified]);

  // analytics
  useEffect(() => {
    getCachedSearchMarkets();
    getTop10MarketCountryWise();
    dispatch(getLastPerformanceProcessedMonth());
    if (pageQuery && !isLoggedIn) {
      callApi(
        setMarketingTrackingURL,
        {
          queryParams: {
            page: pageQuery,
          },
        },
        false
      );
    }
  }, []);

  // Tutorial logic
  useEffect(() => {
    // Check URL parameter for tutorial
    const urlParams = new URLSearchParams(location.search);
    const tutorialParam = urlParams.get('tutorial');
    if (tutorialParam && isValidTutorialQueryValue(tutorialParam)) {
      setTutorialType(tutorialParam);
      setShowTutorial(true);
      // Remove param from URL without reload
      urlParams.delete('tutorial');
      const newUrl = `${location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
      return;
    }

    // Check for market tutorial when user is on the landing page
    const shouldShowNeighborhoodTutorial =
      localStorage.getItem('show_neighborhood_tutorial') &&
      localStorage.getItem('show_neighborhood_tutorial') === 'true';
    if (shouldShowNeighborhoodTutorial) {
      setTutorialType('neighborhood_research');
      setShowTutorial(true);
      return;
    }
  }, [location.pathname, location.search]);

  // Handle tutorial close
  const handleTutorialClose = () => {
    setShowTutorial(false);
  };

  useEffect(() => {
    let isReturningUser = false;
    const timeNow = moment();
    const lastVisit = localStorage.getItem('visit-time');
    if (lastVisit) {
      const lastVisitMoment = moment(lastVisit);
      const diff = timeNow.diff(lastVisitMoment, 'hours');
      isReturningUser = diff > 1;
    } else {
      localStorage.setItem('visit-time', timeNow.toISOString());
    }

    if (user && user.email != 'Trial' && user.userName != 'Trial') {
      const uniqueUserId: string | null = getUniqueUserId(user);
      try {
        (window as any).hj &&
          (window as any).hj('identify', uniqueUserId, {
            email: user.email,
          });
      } catch (err) {
        console.log('Error while setting up the user email in hotjar: ', err);
      }
    }
  }, [user]);

  // login/signup modal popup logic

  const isCalculatorPage = window.location.pathname.includes('airbnb-calculator') ||
    window.location.search.includes('address-full');

  useEffect(() => {
    if (
      !isLoggedIn &&
      !isHomePage &&
      !isCalculatorPage &&
      !dashboardState.showEstimatorDashboard &&
      !location.pathname.includes('airbnb-calculator') &&
      !location.pathname.includes('tailored/t')
    ) {
      setLoginModalOpen(true);
    }
  }, [isLoggedIn, dashboardState, location.pathname]);

  // api calls
  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  // route checks
  useEffect(() => {
    const path = window.location.pathname;
    if (path === MARKET_HOME_CHAT_PATH || path === `${MARKET_HOME_CHAT_PATH}/`) {
      void handleMarketSidebarClick('home');
      handleSidebarListItemClick(HOME_CHAT_SIDEBAR_LIST_INDEX);
    }
    if (window.location.pathname.includes('my-analysis')) {
      setLanding('my-analysis');
      handleMarketSidebarClick('myAnalysis');
      handleSidebarListItemClick(5);
    }
    if (window.location.pathname.includes('airbnb-calculator')) {
      setLanding('estimator');
      handleMarketSidebarClick('estimator');
      handleSidebarListItemClick(1);
    }
    if (window.location.pathname.includes('markets/compare')) {
      setOverviewDashboardLayoutView('compare');
      handleSidebarListItemClick(2);
    }
    if (window.location.pathname.includes('pricing')) {
      setLanding('dynamic-pricing');
      handleMarketSidebarClick('listings');
      handleSidebarListItemClick(7);
    }
    if (window.location.pathname.includes('settings')) {
      setLanding('settings');
      handleMarketSidebarClick('settings');
      handleSidebarListItemClick(10);
    }
    if (window.location.pathname.includes('advanced-dashboard')) {
      setLanding('advanced-dashboard');
      handleMarketSidebarClick('advance');
      handleSidebarListItemClick(12);
    }
    if (window.location.pathname.includes('search-listing')) {
      setLanding('search-listing');
      handleMarketSidebarClick('searchListing');
    }
    if (isTailoredRegionPage) {
      setIsTailoredRegionOpen(true);
    }
  }, [window.location.pathname, window.location.search]);

  const setTailorRegionLogic = (isTailored: string) => {
    if (isTailored) {
      setIsTailoredRegionOpen(true);
    }
  };

  useEffect(() => setTailorRegionLogic(isTailored));

  // dashboard logic

  const resetCurrencyToDefault = () => {
    setCurrency(getUserPreferredCurrency(currency));
  };

  const closeBookingForm = () => {
    setShowDemoBookingForm(false);
  };

  const openDemoBookingForm = () => {
    setShowDemoBookingForm(true);
  };

  const handleMarketSidebarClick = async (event: string) => {
    let emptyState = {
      showInsightsDashboard: false,
      showEstimatorDashboard: false,
      showSettingsDashboard: false,
      showTailoredRegion: false,
      showSearchListingDashboard: false,
      showHomeDashboard: false,
    };

    if (event === 'main') {
      openOverviewOnboardingPage();
    }
    if (event === 'estimator') {
      emptyState['showEstimatorDashboard'] = true;
    }
    if (event === 'settings') {
      emptyState['showSettingsDashboard'] = true;
      window.history.pushState({}, 'Settings', '/user/settings');
    }
    if (event === 'tailoredRegion') {
      emptyState['showTailoredRegion'] = true;
      setIsTailoredRegionOpen(true);
    }
    if (event === 'listings') {
      emptyState['showDynamicPricingDashboard'] = true;
      const hasUserVisitedDynamicPricing =
        localStorage.getItem('visited-dynamic-pricing') === 'true';
      if (user && user.email && user.email != 'Trial' && !hasUserVisitedDynamicPricing) {
        try {
          const returnedData = await postOnSlack(
            'dynamic-pricing-monitor',
            `${user.email} checked out Dynamic Pricing.\n`
          );
          if (returnedData) {
            window.localStorage.setItem('visited-dynamic-pricing', 'true');
          }
        } catch (err) {
          console.log(err);
        }
      }
    }

    if (event === 'estimator') {
      window.history.pushState(
        {
          address_full: estimatorAddressQuery ? estimatorAddressQuery : estimatorAddressFullQuery,
          address_short: estimatorAddressFshortQuery,
          bedrooms: estimatorBedroomsQuery,
          report_type: estimatorReportTypeQuery,
          property_price: estimatorPropertyPriceQuery,
          monthly_rent: estimatorMontlyRentQuery,
          latitude: estimatorLatQuery,
          longitude: estimatorLngQuery,
        },
        '',
        // '/airbnb-calculator'
        window.location.pathname.includes('airbnb-calculator')
          ? window.location.pathname
          : '/airbnb-calculator'
      );
    } else if (event === 'tailoredRegion') {
      window.history.pushState({}, 'TailoredRegion', '/tailored/t');
    } else if (event === 'compare') {
      emptyState['showInsightsDashboard'] = true;
      setOverviewDashboardLayoutView('compare');
      history.push('/markets/compare');
    } else if (event === 'searchListing') {
      emptyState['showSearchListingDashboard'] = true;
      setOverviewDashboardLayoutView('searchListing');
    } else if (event === 'home') {
      emptyState['showHomeDashboard'] = true;
      window.history.pushState({}, '', MARKET_HOME_CHAT_PATH);
    } else if (event !== 'settings' && event !== 'myAnalysis' && event !== 'compare' && event !== 'home') {
      window.history.pushState({}, '', '/airbnb-data/landing');
    }
    setdashboardState(emptyState);
  };

  const handleSidebarListItemClick = (index: number) => {
    setSelectedSidebarIndex(index);
  };

  if (!country) {
    country = '';
  }

  const OnBoardingSearchBox = (
    source: string,
    showBasicSearchbar: boolean = false,
    handleCustomOnSearchChange: any = null,
    searchbarStyle: 'main' | 'compare' = 'main',
    searchbarType: 'main' | 'compare' = 'main'
  ) => (
    <MarketSearchBar
      search={search}
      showAlert={() => { }}
      notShowAlert={() => { }}
      setdashboardState={setdashboardState}
      user={user}
      district={district}
      isUserVerified={isUserVerified}
      isOnboardingOn={true}
      isMobileScreen={matches}
      cachedSearchMarkets={{ ...cachedSearchMarkets, userPrefCountryCode: searchBaseCC }}
      source={source}
      showBasicSearchbar={showBasicSearchbar}
      handleCustomOnSearchChange={handleCustomOnSearchChange}
      searchbarStyle={searchbarStyle}
      searchbarType={searchbarType}
      cachedTop10MarketCountryWise={cachedTop10MarketCountryWise}
      setOverviewDashboardLayoutView={setOverviewDashboardLayoutView}
      dummyMarket={dummyMarket}
      handleOnSampleClick={handleOnSampleClick}
      setIsTailoredRegionOpen={setIsTailoredRegionOpen}
    />
  );

  // SEO

  let SEODistrictName = search.data?.name || district;
  let SEOCountryName = search.data?.country || country;
  let SEOCityName = cityName && cityName != '0' ? ', ' + cityName : '';
  let SEOrecommendPageURL0 =
    'https://app.airbtics.com/airbnb-data/united-states/ca/san%20francisco';
  let SEOrecommendPageURL1 = 'https://app.airbtics.com/airbnb-data/united-states/fl/miami';
  let SEOrecommendPageDistrictName0 = 'San Francisco';
  let SEOrecommendPageDistrictName1 = 'Miami';
  try {
    if (search && search.data && search.data.recommendPageURLs) {
      SEOrecommendPageURL0 = search.data.recommendPageURLs[0];
      SEOrecommendPageDistrictName0 = SEOrecommendPageURL0.split('/').pop() || '';
      if (search.data.recommendPageURLs.length > 1) {
        SEOrecommendPageURL1 = search.data.recommendPageURLs[1];
        SEOrecommendPageDistrictName1 = SEOrecommendPageURL1.split('/').pop() || '';
      }
    }
  } catch (error) {
    console.log('Error while getting recommend page for SEO ', error);
  }

  const getOverviewLandingPageComponent = () => {
    return (
      <OverviewLandingPage
        SearchBox={OnBoardingSearchBox}
        handleMarketSidebarClick={handleMarketSidebarClick}
        dummyMarket={dummyMarket}
        handleOnSampleClick={handleOnSampleClick}
        currency={currency}
        resetCurrencyToDefault={resetCurrencyToDefault}
        isLoggedIn={isLoggedIn}
        dummyMarketID={dummyMarket.id}
      />
    );
  };

  const getEstimatorDashboardComponent = (searchbarOnly: boolean = false) => {
    return (
      <EstimatorPage
        setSubscriptionModalOpen={setSubscriptionModalOpen}
        user={user}
        isLoggedIn={isLoggedIn}
        loginModalOpen={loginModalOpen}
        setLoginModalOpen={setLoginModalOpen}
        matches={matches}
        currency={currency}
        setIsEmailVerifyModalOpen={setIsEmailVerifyModalOpen}
        googleAutocompleteSessionToken={googleAutocompleteSessionToken}
        getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
        clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
        googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
        dummyMarket={dummyMarketEstimator}
        dummyOverviewMarket={dummyMarket}
        loginCountryCode={loginCountryCode}
        setEstimatorSimplePricingModalViewMode={setRequiredSubscriptionPlan}
        setShowTrial={setShowTrial}
        handleOnSampleClick={handleOnSampleClick}
        resetCurrencyToDefault={resetCurrencyToDefault}
        OnBoardingSearchBox={OnBoardingSearchBox}
        openComparePage={openReportComparePage}
        setOpenComparePage={setOpenReportComparePage}
        searchbarOnly={searchbarOnly}
        setHeaderExtraContent={setHeaderExtraContent}
      />
    );
  };

  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  const [tutorialType, setTutorialType] = useState<TutorialType>('market_explorer');

  return (
    <>
      <a
        href={SEOrecommendPageURL0}
        className={classes.SEOLinkStyle}
        tabIndex={-1}
        aria-hidden="true"
      >
        {`See short-term rental data in ${SEOrecommendPageDistrictName0}`}
      </a>
      <a
        href={SEOrecommendPageURL1}
        className={classes.SEOLinkStyle}
        tabIndex={-1}
        aria-hidden="true"
      >
        {`See Airbnb occupancy, daily rate and revenue data in ${SEOrecommendPageDistrictName1}`}
      </a>

      <DemoBookingForm
        matches={matches}
        user={user}
        showDemoBookingForm={showDemoBookingForm}
        closeBookingForm={closeBookingForm}
        headingFor={demoFormHeadingFor}
      />

      <Header
        setLoginModalOpen={setLoginModalOpen}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        dashboardState={dashboardState}
        setdashboardState={setdashboardState}
        isMobile={matches}
        handleMarketSidebarClick={handleMarketSidebarClick}
        setSubscriptionModalOpen={setSubscriptionModalOpen}
        district={district}
        isUserVerified={isUserVerified}
        cachedSearchMarkets={{ ...cachedSearchMarkets, userPrefCountryCode: searchBaseCC }}
        handleSidebarListItemClick={handleSidebarListItemClick}
        centerCoord={centerCoord}
        address={address}
        dashboardLayoutView={overviewDashboardLayoutView}
        openDemoBookingForm={openDemoBookingForm}
        setDemoFormHeadingFor={setDemoFormHeadingFor}
        dummyMarket={dummyMarket}
        setOverviewDashboardLayoutView={setOverviewDashboardLayoutView}
        handleOnSampleClick={handleOnSampleClick}
        setIsTailoredRegionOpen={setIsTailoredRegionOpen}
        headerExtraContent={headerExtraContent}
      />

      <Sidebar
        parent="market"
        handleSidebarClick={handleMarketSidebarClick}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        matches={matches}
        selectedSidebarIndex={selectedSidebarIndex}
        handleSidebarListItemClick={handleSidebarListItemClick}
        currency={currency}
        setCurrency={setCurrency}
        isUserVerified={isUserVerified}
        setIsEmailVerifyModalOpen={setIsEmailVerifyModalOpen}
        dashboardState={dashboardState}
        user={user}
        isLoggedIn={isLoggedIn}
        setOpenReportComparePage={setOpenReportComparePage}
      />

      {isTailoredRegionOpen && (
        <OnboardingModalMobile
          getTokenThunk={getTokenThunk}
          setIsTailoredRegionOpen={setIsTailoredRegionOpen}
          tailoredRegionMarkers={tailoredRegionMarkers}
          getTailoredRegionMarkers={getTailoredRegionMarkers}
        />
      )}

      {loginModalOpen && !isLoggedIn && (
        <AuthModal
          authModalOpen={loginModalOpen}
          setAuthModalOpen={setLoginModalOpen}
          getUserInfo={getUserInfo}
          getTokenThunk={getTokenThunk}
          matches={matches}
          landing={landing}
        />
      )}

      {subscriptionModalOpen && (
        <SubscriptionModal
          countryCode={search.data.countryCode}
          city={search.data.city}
          district={search.data.name}
          getTokenThunk={getTokenThunk}
          subscriptionModalOpen={subscriptionModalOpen}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          setIsTailoredRegionOpen={setIsTailoredRegionOpen}
          user={user}
          setShowUpgradeSubscriptionModal={setShowUpgradeSubscriptionModal}
          basicViewPlanName={requiredSubscriptionPlan}
          view={subscriptionModalView}
          setView={setSubscriptionModalView}
          origin={subscriptionModalOrigin}
          showTrial={showTrial}
          setRequiredSubscriptionPlan={setRequiredSubscriptionPlan}
          setSubscriptionModalView={setSubscriptionModalView}
          setSubscriptionModalOrigin={setSubscriptionModalOrigin}
          resetSubscriptionModal={resetSubscriptionModal}
        />
      )}

      {showUpgradeSubscriptionModal && (
        <UpgradeSubscriptionModal
          plan={showUpgradeSubscriptionModal}
          setPlan={setShowUpgradeSubscriptionModal}
          resetSubscriptionModal={resetSubscriptionModal}
        />
      )}

      {showUpgradeYearlySubscriptionModal && (
        <UpgradeYearlyPlanModal
          user={user}
          setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
        />
      )}

      {(isOverviewLanding || isIndividualLandingPage || isIndividualHostPage) && (
        <>
          <main className={classes.root}>
            <Container
              style={{
                paddingTop: HEADER_HEIGHT,
                maxWidth: 4600,
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 0,
              }}
            >
              {getOverviewLandingPageComponent()}

              {isIndividualLandingPage && (
                <IndividualListingPage
                  user={user}
                  currency={currency}
                  isLoggedIn={isLoggedIn}
                  subscriptionModalOpen={subscriptionModalOpen}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  handleOnSampleClick={handleOnSampleClick}
                  openDemoBookingForm={openDemoBookingForm}
                  setDemoFormHeadingFor={setDemoFormHeadingFor}
                  marketID={''}
                />
              )}

              {isIndividualHostPage && (
                <IndividualHostPage
                  user={user}
                  currency={currency}
                  isLoggedIn={isLoggedIn}
                  subscriptionModalOpen={subscriptionModalOpen}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  handleOnSampleClick={handleOnSampleClick}
                  isMobileScreen={matches}
                />
              )}
            </Container>
          </main>
        </>
      )}

      {!isHomePage && !isOverviewLanding && !isIndividualLandingPage && !isIndividualHostPage && (
        <>
          <main
            className={classes.root}
            id="market-dashboard-main"
            style={
              isCalculatorPage ||
              dashboardState.showEstimatorDashboard ||
              dashboardState.showHomeDashboard
                ? { overflow: 'visible' }
                : undefined
            }
          >
            <Container
              style={{
                paddingTop: HEADER_HEIGHT,
                maxWidth: 4600,
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 0,
              }}
            >
              {dashboardState.showInsightsDashboard && (
                <OverviewPage
                  search={search}
                  inputMarketName={district}
                  SEODistrictName={SEODistrictName}
                  SEOCountryName={SEOCountryName}
                  SEOCityName={SEOCityName}
                  dashboardLayoutView={overviewDashboardLayoutView}
                  setDashboardLayoutView={setOverviewDashboardLayoutView}
                  matches={matches}
                  subscriptionModalOpen={subscriptionModalOpen}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  currency={currency}
                  user={user}
                  setShowSearchCreditDeductWarningModal={setShowSearchCreditDeductWarningModal}
                  isLoggedIn={isLoggedIn}
                  handleOnSampleClick={handleOnSampleClick}
                  handleMarketSidebarClick={handleMarketSidebarClick}
                  handleSidebarListItemClick={handleSidebarListItemClick}
                  openDemoBookingForm={openDemoBookingForm}
                  setDemoFormHeadingFor={setDemoFormHeadingFor}
                  dummyMarket={dummyMarket}
                  loginCountryCode={loginCountryCode}
                  dataStoreTimePeriodQuery={dataStoreTimePeriodQuery}
                  dataStoreTimeGranularityQuery={dataStoreTimeGranularityQuery}
                  getTokenThunk={getTokenThunk}
                  tailoredRegionMarkers={tailoredRegionMarkers}
                  getTailoredRegionMarkers={getTailoredRegionMarkers}
                  OnBoardingSearchBox={OnBoardingSearchBox}
                  cachedMarketData={cachedMarketData}
                  getEstimatorDashboardComponent={getEstimatorDashboardComponent}
                  isTailoredRegionOpen={isTailoredRegionOpen}
                  setIsTailoredRegionOpen={setIsTailoredRegionOpen}
                  setLoginModalOpen={setLoginModalOpen}
                />
              )}

              {dashboardState.showEstimatorDashboard && getEstimatorDashboardComponent()}


              {dashboardState.showSettingsDashboard && (
                <SettingsDashboardV2
                  user={user}
                  isMobileView={matches}
                  setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
                  currency={currency}
                  setCurrency={setCurrency}
                  OverviewLandingPageComponent={getOverviewLandingPageComponent()}
                  EstimatorDashboardComponent={getEstimatorDashboardComponent()}
                  isLoggedIn={isLoggedIn}
                  setLoginModalOpen={setLoginModalOpen}
                  isMobileScreen={matches}
                  resetCurrencyToDefault={resetCurrencyToDefault}
                />
              )}
              {dashboardState.showSearchListingDashboard && (
                <SearchListings
                  currency={currency}
                  resetCurrencyToDefault={resetCurrencyToDefault}
                  setSubscriptionModalOpen={setSubscriptionModalOpen}
                  handleOnSampleClick={handleOnSampleClick}
                  subscriptionModalOpen={subscriptionModalOpen}
                  openDemoBookingForm={openDemoBookingForm}
                  setDemoFormHeadingFor={setDemoFormHeadingFor}
                  user={user}
                  matches={matches}
                />
              )}
              {dashboardState.showHomeDashboard && (
                <HomeChatPage
                  user={user}
                  matches={matches}
                  isLoggedIn={isLoggedIn}
                  setLoginModalOpen={setLoginModalOpen}
                />
              )}
            </Container>
          </main>
        </>
      )}

      {isLoggedIn && isEmailVerifyModalOpen && !user.isVerified && !user.loading && (
        <EmailVerifyModal
          open={isEmailVerifyModalOpen}
          setOpen={setIsEmailVerifyModalOpen}
          email={user.email}
          matches={matches}
        />
      )}

      {showTutorial && (
        <TutorialModal open={showTutorial} onClose={handleTutorialClose} type={tutorialType} />
      )}
    </>
  );
};

MarketDashboard.propTypes = {
  getTokenThunk: PropTypes.func,
  getUserInfo: PropTypes.func,
};

export default MarketDashboard;
